import { useEffect, useState } from "react";

/**
 * How long to wait before admitting a request is taking longer than it should.
 *
 * This used to say "Waking up the database -- this can take up to 20 seconds after a quiet period",
 * which was true of Aurora Serverless at minCapacity 0. Aurora is gone: measured cold start on DynamoDB
 * is ~1.0s and warm is ~0.15s. That message now only ever appears when something is genuinely wrong,
 * and it told the user to sit and wait for a resume that will never happen.
 *
 * The threshold is longer than it was for the same reason -- 2.5s used to be "definitely a cold start",
 * and is now well outside normal, so anything past 6s is worth acknowledging rather than explaining.
 */
const SLOW_THRESHOLD_MS = 6000;

export function LoadingState({ label = "Loading…" }: { label?: string }) {
  const [slow, setSlow] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setSlow(true), SLOW_THRESHOLD_MS);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="loading-state" role="status" aria-live="polite">
      <span className="spinner" aria-hidden="true" />
      <span>{slow ? "Still working on it — this is taking longer than usual." : label}</span>
    </div>
  );
}

/** Content-shaped placeholder for list views, so the layout doesn't jump when real rows land. */
export function SkeletonList({ rows = 3 }: { rows?: number }) {
  return (
    <ul className="client-list" aria-hidden="true">
      {Array.from({ length: rows }, (_, i) => (
        <li key={i} className="client-card skeleton-card">
          <span className="skeleton skeleton-avatar" />
          <span className="skeleton skeleton-line" />
        </li>
      ))}
    </ul>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const message = error instanceof Error ? error.message : "Something went wrong";
  return (
    <div className="error-state" role="alert">
      <p className="error">{message}</p>
      {onRetry && (
        <button type="button" className="chip" onClick={onRetry}>
          Try again
        </button>
      )}
    </div>
  );
}
